/**
 * Per-user table and chrome preferences for the orders dashboard.
 *
 * Everything here is a pure function over a storage-like object so the rules can
 * be tested without a DOM, a store or a component. The component layer decides
 * *when* to ask; this file decides *what the answer is*.
 */

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const SIDEBAR_PREF_KEY = 'sidebarOpen';
export const COLUMN_PREF_KEY = 'dashboardColumns';
/** AB#21534 — the Users page keeps its own column choice. */
export const USERS_COLUMN_PREF_KEY = 'usersColumns';
/** AB#21536 — so do Roles and Permissions. */
export const ROLES_COLUMN_PREF_KEY = 'rolesColumns';
export const PERMISSIONS_COLUMN_PREF_KEY = 'permissionsColumns';
/** AB#21541 */
export const CUSTOMERS_COLUMN_PREF_KEY = 'customersColumns';
/** AB#21963 */
export const DESIGN_CATEGORIES_COLUMN_PREF_KEY = 'designCategoriesColumns';
/** AB#22184 */
export const DELIVERY_BOXES_COLUMN_PREF_KEY = 'deliveryBoxesColumns';
/** AB#21959 */
export const KICKBACK_COLUMN_PREF_KEY = 'kickbackColumns';
/** AB#21970 */
export const PRICE_LISTS_COLUMN_PREF_KEY = 'priceListsColumns';
/** AB#22003 */
export const COMPANIES_COLUMN_PREF_KEY = 'companiesColumns';
// AB#21971 — CRM → Quotes.
export const QUOTES_COLUMN_PREF_KEY = 'crmQuotesColumns';

/* ------------------------------------------------------------------ *
 * Sidebar
 * ------------------------------------------------------------------ */

/**
 * Whether the sidebar should be expanded on first paint.
 *
 * AB#21537 — always collapsed. The menu used to start collapsed on the
 * dashboard only, and a stored "expanded" survived a refresh everywhere; the
 * product decision now is that every page load starts with the rail, and a
 * person who wants the full menu opens it for this visit. The stored value is
 * still written (rememberSidebarState) so the order pane can hand back what
 * it displaced within a visit, but it no longer decides the first paint.
 */
export const sidebarShouldBeOpen = (storage: StorageLike | null | undefined): boolean => {
  void storage;
  return false;
};

/** Record the user's own choice. Only call this from a real user gesture. */
export const rememberSidebarState = (
  storage: StorageLike | null | undefined,
  open: boolean
): void => {
  writeRaw(storage, SIDEBAR_PREF_KEY, open ? 'true' : 'false');
};

/**
 * What the sidebar should be after the order detail pane closes.
 *
 * Opening an order collapses the menu to make room for the pane. Closing it
 * used to set the menu back to *expanded* unconditionally, which overruled
 * anyone who had deliberately collapsed it — open one order, close it, and the
 * menu you put away came back.
 *
 * `displaced` is what the menu was at the moment the pane took the space.
 * `null` means this page never opened the pane itself — a refresh with an order
 * already open — and then there is nothing to restore, so the stored preference
 * decides.
 */
export const sidebarStateAfterPaneClose = (
  displaced: boolean | null | undefined,
  storage: StorageLike | null | undefined
): boolean =>
  displaced === null || displaced === undefined ? sidebarShouldBeOpen(storage) : displaced;

/* ------------------------------------------------------------------ *
 * Page size
 * ------------------------------------------------------------------ */

/** Chrome above and below the rows: toolbars, sticky header, pagination footer. */
export const TABLE_CHROME_PX = 300;
/** One compact row. Keep in step with the `h-11` on the density:compact cells. */
export const COMPACT_ROW_PX = 44;

/**
 * How many rows to ask the API for so the table fills the window it is in.
 *
 * A fixed page size of 10 leaves roughly half of a 900px window empty and the
 * other half of a 1400px one unused. `options` are the sizes the select offers;
 * the answer is always one of them, because the number shown in the select and
 * the number sent to the API have to be the same value.
 */
export const pageSizeForViewport = (
  viewportHeight: number,
  options: number[] = [10, 20, 50, 100],
  rowHeight: number = COMPACT_ROW_PX,
  chrome: number = TABLE_CHROME_PX
): number => {
  const sorted = [...options].sort((a, b) => a - b);
  if (sorted.length === 0) return 10;

  // Only NaN needs special handling: it propagates silently through every
  // comparison below and would make the loop pick nothing. An implausibly large
  // height is not nonsense in the same way — it should simply want more rows.
  const height = Number.isNaN(viewportHeight) ? 0 : viewportHeight;
  const usable = Math.max(0, height - chrome);
  const fits = Math.floor(usable / Math.max(1, rowHeight));

  // Never ask for fewer than the smallest option — a short window should still
  // return a usable page rather than one row.
  if (fits <= sorted[0]) return sorted[0];

  // The largest option that still fits, so the last row is not half cut off.
  let best = sorted[0];
  for (const size of sorted) {
    if (size <= fits) best = size;
  }
  return best;
};

/* ------------------------------------------------------------------ *
 * Column visibility
 * ------------------------------------------------------------------ */

/**
 * Columns that have been renamed since a preference could have been saved.
 * A stored choice keeps working across the rename; without this, hiding "Date"
 * last week would silently stop applying the day it became "Created".
 */
const RENAMED_COLUMNS: Record<string, string> = {
  Date: 'Created', // AB#21297
  'Est. Shipping Date': 'Est. Shipping', // AB#21576
};

/**
 * Columns the user has chosen to hide, by label.
 *
 * Stored by label rather than index because the column set is permission-driven
 * and changes shape between users and between releases; an index would silently
 * point at a different column the day someone gains a permission.
 */
export const readHiddenColumns = (
  storage: StorageLike | null | undefined,
  key: string = COLUMN_PREF_KEY
): string[] => {
  const raw = readRaw(storage, key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return Array.from(
      new Set(
        parsed
          .filter((entry): entry is string => typeof entry === 'string' && entry !== '')
          .map((entry) => RENAMED_COLUMNS[entry] ?? entry)
      )
    );
  } catch {
    return [];
  }
};

export const writeHiddenColumns = (
  storage: StorageLike | null | undefined,
  hidden: string[],
  key: string = COLUMN_PREF_KEY
): void => {
  const clean = Array.from(
    new Set((hidden ?? []).filter((entry) => typeof entry === 'string' && entry !== ''))
  );
  writeRaw(storage, key, JSON.stringify(clean));
};

export interface HideableColumn {
  label?: string;
  /** Columns that carry the row's identity or its actions cannot be hidden. */
  alwaysVisible?: boolean;
}

/**
 * Apply the stored preference to a live column set.
 *
 * Two rules stop the picker from producing an unusable table:
 *   - a column marked `alwaysVisible` is never removed, whatever is stored;
 *   - if every column would be hidden, the preference is ignored entirely. An
 *     empty table looks broken, and the user cannot get the picker back from it
 *     if the picker lives in the table's own header.
 */
export const applyColumnPreference = <T extends HideableColumn>(
  columns: (T | false | null | undefined)[],
  hidden: string[]
): T[] => {
  const present = (columns ?? []).filter((column): column is T => !!column);
  if (!hidden || hidden.length === 0) return present;

  const hiddenSet = new Set(hidden);
  const kept = present.filter(
    (column) => column.alwaysVisible === true || !hiddenSet.has(column.label ?? '')
  );

  return kept.length === 0 ? present : kept;
};

/** The labels a picker should offer, i.e. everything the user is allowed to hide. */
export const hideableLabels = <T extends HideableColumn>(
  columns: (T | false | null | undefined)[]
): string[] =>
  (columns ?? [])
    .filter((column): column is T => !!column)
    .filter((column) => column.alwaysVisible !== true)
    .map((column) => column.label ?? '')
    .filter((label) => label !== '');

/* ------------------------------------------------------------------ *
 * Column widths (AB#21576)
 * ------------------------------------------------------------------ */

/** Narrower than this and a column is a sliver nobody can read or grab. */
export const MIN_COLUMN_PX = 60;
/** Wider than this and one column is the whole screen. */
export const MAX_COLUMN_PX = 1600;
/** A pinned column may take at most this share of the grid, or it covers it. */
export const PINNED_MAX_FRACTION = 0.5;

/** Widths chosen by the user, in pixels, keyed by the column's `key`. */
export type ColumnWidths = Record<string, number>;

/**
 * The storage key for widths, derived from the page's column-visibility key so
 * the two preferences of one grid sit side by side: `dashboardColumns` and
 * `dashboardColumnWidths`, `usersColumns` and `usersColumnWidths`.
 */
export const columnWidthsKey = (columnPrefKey: string = COLUMN_PREF_KEY): string =>
  columnPrefKey.endsWith('Columns')
    ? `${columnPrefKey.slice(0, -'Columns'.length)}ColumnWidths`
    : `${columnPrefKey}Widths`;

/** Snap a requested width into the range a column is allowed to have. */
export const clampColumnWidth = (
  px: number,
  min: number = MIN_COLUMN_PX,
  max: number = MAX_COLUMN_PX
): number => {
  if (!Number.isFinite(px)) return min;
  return Math.round(Math.min(Math.max(px, min), Math.max(min, max)));
};

/**
 * Stored widths, keyed by column key rather than label: keys are stable
 * identifiers, labels are copy that gets rewritten. Anything that is not a
 * finite positive number is dropped, so a hand-edited or stale value cannot
 * collapse a column to nothing.
 */
export const readColumnWidths = (
  storage: StorageLike | null | undefined,
  key: string = columnWidthsKey()
): ColumnWidths => {
  const raw = readRaw(storage, key);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: ColumnWidths = {};
    for (const [column, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (column === '' || typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        continue;
      }
      out[column] = clampColumnWidth(value);
    }
    return out;
  } catch {
    return {};
  }
};

/** An empty map removes the entry rather than storing `{}` forever. */
export const writeColumnWidths = (
  storage: StorageLike | null | undefined,
  widths: ColumnWidths,
  key: string = columnWidthsKey()
): void => {
  const clean: ColumnWidths = {};
  for (const [column, value] of Object.entries(widths ?? {})) {
    if (column !== '' && typeof value === 'number' && Number.isFinite(value) && value > 0) {
      clean[column] = clampColumnWidth(value);
    }
  }
  if (Object.keys(clean).length === 0) {
    removeRaw(storage, key);
    return;
  }
  writeRaw(storage, key, JSON.stringify(clean));
};

/**
 * Whether "Reset columns to default" has anything to do: a hidden column or a
 * width that differs from the code's own.
 */
export const hasColumnCustomisation = (hidden: string[], widths: ColumnWidths): boolean =>
  (hidden?.length ?? 0) > 0 || Object.keys(widths ?? {}).length > 0;

/* ------------------------------------------------------------------ *
 * Storage access
 * ------------------------------------------------------------------ */

/**
 * localStorage throws rather than returning null in a few real situations —
 * Safari private browsing, storage disabled by policy, quota exhausted. A
 * preference read must never be able to take the dashboard down with it.
 */
const readRaw = (storage: StorageLike | null | undefined, key: string): string | null => {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const writeRaw = (storage: StorageLike | null | undefined, key: string, value: string): void => {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    /* A preference that cannot be saved is not worth an error to the user. */
  }
};

const removeRaw = (storage: StorageLike | null | undefined, key: string): void => {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* As above. */
  }
};
